/**
 * The multiplayer wire format, shared by the game and the realtime Worker
 * (`realtime/worker.ts`).
 *
 * JSON, and small: a client says where it is about once a second, and the
 * room answers with everyone once a second. Cloudflare bills a Durable
 * Object's incoming WebSocket messages, not its outgoing ones, so the rate a
 * client sends at is the cost; what the room sends back is free.
 *
 * Pure and DOM-free, so the Worker can bundle it and the tests can run it.
 */

import type { PlayerMode } from '../config.js';
import type { FallStart } from '../core/character/fall.js';
import { FALL_KINDS } from '../core/character/fall.js';

export const PROTOCOL_VERSION = 1;

/** Client → room. */
export type ClientMessage =
  | { t: 'hi'; v: number; name: string }
  /**
   * `alt`, metres above sea level: the feet on foot and under a canopy, the
   * machine's base in a car or a helicopter.
   */
  | { t: 'at'; lat: number; lon: number; alt: number; hdg: number; mode: PlayerMode; fall?: WireFall }
  /** Still connected and still seeing everyone, but not shown to them. */
  | { t: 'hide' };

/**
 * A player down (see `core/character/ragdoll.ts`, `FallStart`), for others
 * to see them fall as they did: which way they went down (an index into
 * `FALL_KINDS`), the velocity they went at, scene x up z (m/s, to a
 * decimetre a second), the way a bike faced, and how high a bumper struck.
 * Sent with every position while they are down; its going is them getting up.
 */
export type WireFall = [kind: number, vx: number, vy: number, vz: number, fx: number, fz: number, bumper: number];

/**
 * One player as the room sends them: id, name, lat, lon, altitude, heading,
 * mode, when the room heard that position (its clock, ms), and, while they
 * are down, how they fell. A client that knows nothing of falls reads the
 * first eight.
 */
export type PlayerRow = [id: string, name: string, lat: number, lon: number, alt: number, hdg: number, mode: PlayerMode, at: number, fall?: WireFall];

/** Room → client. */
export type RoomMessage =
  | { t: 'welcome'; id: string }
  /** `now` is the room's clock, for placing each row's `at` on the client's. */
  | { t: 'all'; online: number; now: number; players: PlayerRow[] };

const MODES: ReadonlySet<string> = new Set<PlayerMode>(['walk', 'freefall', 'drive', 'moto', 'heli']);

/** A player's name as others see it: printable, trimmed, at most 20 characters. */
export function cleanName(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  // eslint-disable-next-line no-control-regex
  return raw.replace(/[\u0000-\u001f\u007f<>]/g, '').trim().slice(0, 20);
}

/**
 * Parse and check a client's message. Anything malformed, out of range or
 * oversized is undefined, and the room ignores it: a bad client can only
 * fail to be seen.
 */
export function parseClientMessage(data: unknown): ClientMessage | undefined {
  if (typeof data !== 'string' || data.length > 512) return undefined;
  let m: unknown;
  try { m = JSON.parse(data); } catch { return undefined; }
  if (typeof m !== 'object' || m === null) return undefined;
  const o = m as Record<string, unknown>;
  const num = (v: unknown, lo: number, hi: number) => typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;
  switch (o.t) {
    case 'hi':
      return typeof o.v === 'number' ? { t: 'hi', v: o.v, name: cleanName(o.name) } : undefined;
    case 'at':
      if (!num(o.lat, -85, 85) || !num(o.lon, -180, 180) || !num(o.alt, -500, 12000) || !num(o.hdg, -720, 720)) return undefined;
      if (typeof o.mode !== 'string' || !MODES.has(o.mode)) return undefined;
      {
        const at: ClientMessage = { t: 'at', lat: o.lat as number, lon: o.lon as number, alt: o.alt as number, hdg: o.hdg as number, mode: o.mode as PlayerMode };
        // A fall that does not parse is left off, not the position with it.
        const fall = parseWireFall(o.fall);
        if (fall !== undefined) at.fall = fall;
        return at;
      }
    case 'hide':
      return { t: 'hide' };
    default:
      return undefined;
  }
}

/** A fall as sent, checked: undefined if it is not one. */
export function parseWireFall(w: unknown): WireFall | undefined {
  if (!Array.isArray(w) || w.length !== 7 || !w.every(v => typeof v === 'number' && Number.isFinite(v))) return undefined;
  const [kind, vx, vy, vz, fx, fz, bumper] = w as number[];
  if (!Number.isInteger(kind) || kind < 0 || kind >= FALL_KINDS.length) return undefined;
  if ([vx, vy, vz].some(v => Math.abs(v) > 100) || Math.abs(fx) > 1.01 || Math.abs(fz) > 1.01 || bumper < 0 || bumper > 2) return undefined;
  return [kind, vx, vy, vz, fx, fz, bumper];
}

/** A fall for the wire: velocities to a decimetre a second, directions to a hundredth. */
export function fallToWire(f: FallStart): WireFall {
  const r = (v: number, d: number) => Math.round(v * d) / d;
  const [fx, fz] = f.forward ?? [0, 0];
  return [FALL_KINDS.indexOf(f.kind), r(f.v[0], 10), r(f.v[1], 10), r(f.v[2], 10), r(fx, 100), r(fz, 100), r(f.bumper ?? 0.5, 100)];
}

/** A fall from the wire. */
export function fallFromWire(w: WireFall): FallStart {
  const [kind, vx, vy, vz, fx, fz, bumper] = w;
  return { kind: FALL_KINDS[kind], v: [vx, vy, vz], forward: fx === 0 && fz === 0 ? undefined : [fx, fz], bumper };
}

/** A position rounded for the wire: a decimetre, a decimetre, a degree; and a fall, if they are down. */
export function row(id: string, name: string, lat: number, lon: number, alt: number, hdg: number, mode: PlayerMode, at: number, fall?: WireFall): PlayerRow {
  const r = (v: number, d: number) => Math.round(v * d) / d;
  // 1e6 degrees is about ten centimetres: fine enough to walk smoothly.
  const out: PlayerRow = [id, name, r(lat, 1e6), r(lon, 1e6), r(alt, 10), Math.round(((hdg % 360) + 360) % 360), mode, Math.round(at)];
  if (fall !== undefined) out.push(fall);
  return out;
}

export function parseRoomMessage(data: unknown): RoomMessage | undefined {
  if (typeof data !== 'string') return undefined;
  try {
    const m = JSON.parse(data) as RoomMessage;
    if (m.t === 'welcome' && typeof m.id === 'string') return m;
    if (m.t === 'all' && Array.isArray(m.players)) return m;
  } catch { /* ignored */ }
  return undefined;
}

/** A friendly default name: "Swift Otter 42". */
export function randomName(random: () => number = Math.random): string {
  const a = ['Swift', 'Quiet', 'Brave', 'Sunny', 'Lucky', 'Misty', 'Happy', 'Clever', 'Wild', 'Calm', 'Bold', 'Merry'];
  const b = ['Otter', 'Fox', 'Heron', 'Moose', 'Lynx', 'Hare', 'Owl', 'Badger', 'Seal', 'Crane', 'Wolf', 'Robin'];
  const pick = <T>(xs: T[]) => xs[Math.floor(random() * xs.length) % xs.length];
  return `${pick(a)} ${pick(b)} ${10 + Math.floor(random() * 90)}`;
}
